import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX,
  FiCheck,
  FiPhone,
  FiMail,
  FiUser,
  FiBriefcase,
  FiChevronDown,
  FiSearch,
  FiMessageCircle,
} from 'react-icons/fi';
import './App.css';
import HeroSection from './components/HeroSection';
import WorkshopTimer from './components/WorkshopTimer';
import RecentRegistrations from './components/RecentRegistrations';
import { useWorkshopSchedule } from './hooks/useWorkshopSchedule';

// UPDATED: New Payment page URL
const PAYMENT_PAGE_BASE_URL = 'https://paymentz.aifunnels.app/product/21362/page/32154/addon-page';

// WEBHOOK: Pabbly Webhook URL
const WEBHOOK_URL = 'https://connect.pabbly.com/webhook-listener/webhook/IjU3NjIwNTY1MDYzMDA0MzQ1MjZmIg_3D_3D_pc/IjU3NjcwNTZjMDYzNzA0MzA1MjZlNTUzNjUxMzEi_pc';

// WhatsApp Support Number
const WHATSAPP_NUMBER = '+919940890569';
const WHATSAPP_MESSAGE = encodeURIComponent('Hi! I need help with the 10X AI Agent Workshop registration.');

const sectionFadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: 'easeInOut' },
  viewport: { once: true, amount: 0.2 },
};

const countryCodes = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'USA', flag: '🇺🇸' },
  { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+44', country: 'GB', name: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
];

const faqData = [
  {
    id: 1,
    question: 'When and where is this workshop happening?',
    answer: "This is a 3-hour LIVE online workshop held **every Saturday at 11:00 AM IST**. Once you register, you'll receive the secure joining link via email and WhatsApp.",
  },
  {
    id: 2,
    question: 'Is this suitable for new Coaches/Trainers?',
    answer: "Absolutely. Whether you are an aspiring NLP trainer, a Life Coach starting out, or an experienced healer, this workshop covers the fundamentals of automating your client acquisition using AI.",
  },
  {
    id: 3,
    question: 'Do I need technical knowledge or coding skills?',
    answer: 'No. This workshop is designed for non-technical coaches. We guide you through user-friendly AI tools that handle the "tech stuff" for you.',
  },
  {
    id: 4,
    question: "Will I get recordings if I can't attend live?",
    answer: 'To maintain the exclusivity and high-impact nature of the live session, recordings will not be provided. Live attendance is required.',
  },
  {
    id: 5,
    question: 'How will this help get me more clients?',
    answer: 'We teach you how to set up an "AI Agent" that works 24/7 to nurture leads, answer inquiries, and book appointments for you automatically.',
  },
];

const getTrackingData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    campaign_id: urlParams.get('campaign_id') || '',
    adset_id: urlParams.get('adset_id') || '',
    ad_id: urlParams.get('ad_id') || '',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || '',
  };
};

function SearchableCountryCodeSelect({ value, onChange, options }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const selectedCountry = useMemo(
    () => options.find((c) => c.code === value) || options[0],
    [value, options]
  );
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.code.includes(searchTerm));
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <button type="button" className="country-select-button" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedCountry.flag} {selectedCountry.code}</span>
        <FiChevronDown />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div className="dropdown-menu" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input type="text" placeholder="Search..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
            <ul className="dropdown-list">
              {filteredOptions.map(c => (
                <li key={c.country} className="country-option" onClick={() => { onChange(c.code); setIsOpen(false); }}>
                  {c.flag} {c.name} ({c.code})
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+91', phone: '', profession: '', consent: false });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { workshopDetails } = useWorkshopSchedule();

  useEffect(() => {
    if (typeof fbq !== 'undefined') {
      fbq('track', 'PageView');
    }
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.profession) errors.profession = 'Profession is required';
    if (!formData.consent) errors.consent = 'Required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const tracking = getTrackingData();
      const phoneWithCode = `${formData.countryCode}${formData.phone}`;
      const selectedCountry = countryCodes.find(c => c.code === formData.countryCode)?.name || 'Unknown';

      const payload = {
        ...formData,
        full_phone: phoneWithCode,
        country_name: selectedCountry,
        workshop_date: workshopDetails.formattedDate,
        workshop_time: workshopDetails.timeString,
        workshop_batch: workshopDetails.status,
        ...tracking,
        page_url: window.location.href,
        timestamp: new Date().toISOString()
      };

      // 1. Send data to Pabbly Webhook
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // 2. Track Lead in Meta Pixel
      if (typeof fbq !== 'undefined') {
        fbq('track', 'Lead', {
          content_name: 'AI Workshop Registration',
          value: 114,
          currency: 'INR'
        });
      }

      setShowSuccess(true);
      setIsFormModalOpen(false);

      // 3. Redirect to Payment
      const paymentParams = new URLSearchParams({
        staffId: '65130',
        ac: '1',
        first_name: formData.name,
        email: formData.email,
        phone_no: phoneWithCode,
        profession: formData.profession,
        amount: '114',
        currency: 'INR',
        ...tracking
      });

      setTimeout(() => {
        window.location.href = `${PAYMENT_PAGE_BASE_URL}?${paymentParams.toString()}`;
      }, 1500);

    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app">
      <AnimatePresence>
        {showSuccess && (
          <motion.div className="success-toast" initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }}>
            <FiCheck /> Registration successful! Redirecting to payment...
          </motion.div>
        )}
      </AnimatePresence>

      <RecentRegistrations />
      <HeroSection openRegistration={() => setIsFormModalOpen(true)} />

      <section className="faq-section">
        <div className="container">
          <div className="section-content">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-accordion">
              {faqData.map(faq => (
                <div key={faq.id} className="faq-item">
                  <button className="faq-question" onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}>
                    <span>{faq.question}</span>
                    <FiChevronDown className={expandedFaq === faq.id ? 'expanded' : ''} />
                  </button>
                  <AnimatePresence>
                    {expandedFaq === faq.id && (
                      <motion.div className="faq-answer" initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}>
                        <div className="faq-answer-content">{faq.answer}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <motion.section className="final-cta" {...sectionFadeIn}>
        <div className="container text-center">
          <h2>🔥 Limited Time Offer - Only 50 Seats Available!</h2>
          <p className="cta-subtitle">Workshop Value: ₹2,997 | Today Only: **Just ₹114!**</p>
          <WorkshopTimer className="large" />
          <button className="cta-primary large" onClick={() => setIsFormModalOpen(true)}>
            Yes! Reserve My Seat for ₹114 🚀
          </button>
        </div>
      </motion.section>

      <AnimatePresence>
        {isFormModalOpen && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={(e) => e.target === e.currentTarget && setIsFormModalOpen(false)}>
            <motion.div className="modal-content" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}>
              <div className="modal-header">
                <h3>🚀 Register for the AI Workshop</h3>
                <button className="close-button" onClick={() => setIsFormModalOpen(false)}><FiX /></button>
              </div>
              <form onSubmit={handleSubmit} className="registration-form">
                <div className="form-group">
                  <label><FiUser className="input-icon" /> Full Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Your Name" required />
                </div>
                <div className="form-group">
                  <label><FiMail className="input-icon" /> Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Your Email" required />
                </div>
                <div className="form-group">
                  <label><FiPhone className="input-icon" /> Phone *</label>
                  <div className="phone-input-group">
                    <SearchableCountryCodeSelect options={countryCodes} value={formData.countryCode} onChange={(code) => setFormData({...formData, countryCode: code})} />
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g, '')})} placeholder="Phone number" required />
                  </div>
                </div>
                <div className="form-group">
                  <label><FiBriefcase className="input-icon" /> Profession *</label>
                  <select value={formData.profession} onChange={(e) => setFormData({...formData, profession: e.target.value})} required>
                    <option value="">Select Profession</option>
                    <option value="Coach">Coach / Consultant</option>
                    <option value="Trainer">Trainer / NLP Practitioner</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={formData.consent} onChange={(e) => setFormData({...formData, consent: e.target.checked})} required />
                    I agree to receive updates via Email/WhatsApp
                  </label>
                </div>
                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : 'Register Now for ₹114 🚀'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="footer">
        <div className="container text-center">
          <p>&copy; 2025 10xAI Founder. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;